import { useState } from "react";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Pencil, Trash2, RotateCw, Receipt, CheckCircle2,
  XCircle, Clock, Eye, TrendingUp, IndianRupee, ShoppingBag, Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { SearchBar } from "@/components/common/SearchBar";
import { DataTable, Column } from "@/components/tables/DataTable";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useListState } from "@/hooks/useListState";
import {
  useGetOrdersQuery,
  useUpdateOrderMutation,
  useDeleteOrderMutation,
  Order,
} from "@/redux/slices/orderApi";
import { useGetPlansQuery } from "@/redux/slices/planApi";
import { useGetStudentsQuery } from "@/redux/slices/studentApi";
import { format } from "date-fns";

const schema = z.object({
  userId: z.string().min(1, "Student is required"),
  planId: z.string().min(1, "Plan is required"),
  amount: z.number().min(0, "Amount must be >= 0"),
  paymentStatus: z.enum(["Pending", "Success", "Failed"]),
  paymentMethod: z.string().optional(),
  transactionId: z.string().optional(),
  isActive: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

/* ─── Status helpers ─────────────────────────────────────────────── */
function StatusBadge({ status }: { status: string }) {
  if (status === "Success")
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
        <CheckCircle2 className="h-3.5 w-3.5" /> Success
      </span>
    );
  if (status === "Failed")
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/10 px-2.5 py-1 text-xs font-semibold text-red-600 dark:text-red-400">
        <XCircle className="h-3.5 w-3.5" /> Failed
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
      <Clock className="h-3.5 w-3.5" /> Pending
    </span>
  );
}

/* ─── Stats Card ─────────────────────────────────────────────────── */
function StatCard({
  label,
  value,
  icon: Icon,
  color,
  sub,
}: {
  label: string;
  value: string | number;
  icon: any;
  color: string;
  sub?: string;
}) {
  return (
    <div className="flex items-center gap-4 rounded-xl border bg-card p-5 shadow-sm">
      <div className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-xl", color)}>
        <Icon className="h-6 w-6" />
      </div>
      <div>
        <p className="text-2xl font-bold leading-tight">{value}</p>
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        {sub && <p className="mt-0.5 text-[10px] text-muted-foreground/70">{sub}</p>}
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────── */
export default function OrderHistoryPage() {
  const list = useListState();
  const { data, isFetching, refetch } = useGetOrdersQuery(list.queryParams());
  const { data: plansData } = useGetPlansQuery({});
  const { data: studentsData } = useGetStudentsQuery({});

  const [updateOrder, updateM] = useUpdateOrderMutation();
  const [deleteOrder, deleteM] = useDeleteOrderMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Order | null>(null);
  const [toDelete, setToDelete] = useState<Order | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      userId: "",
      planId: "",
      amount: 0,
      paymentStatus: "Success",
      paymentMethod: "Manual",
      transactionId: "",
      isActive: true,
    },
  });

  const handlePlanChange = (val: string) => {
    form.setValue("planId", val);
    const plan = plansData?.data?.find((p: any) => p.id === val);
    if (plan) form.setValue("amount", plan.price);
  };

  const openEdit = (o: Order) => {
    setEditing(o);
    form.reset({
      userId: o.userId,
      planId: o.planId,
      amount: o.amount,
      paymentStatus: o.paymentStatus,
      paymentMethod: o.paymentMethod || "",
      transactionId: o.transactionId || "",
      isActive: o.isActive,
    });
    setOpen(true);
  };

  const onSubmit = async (v: FormValues) => {
    try {
      if (editing) {
        await updateOrder({ id: editing.id, body: v }).unwrap();
        toast.success("Order updated successfully");
      }
      setOpen(false);
    } catch {
      toast.error("Something went wrong");
    }
  };

  /* ── Computed stats ── */
  const orders: Order[] = data?.data ?? [];
  const totalOrders = (data as any)?.total ?? orders.length;
  const successOrders = orders.filter((o) => o.paymentStatus === "Success");
  const totalRevenue = successOrders.reduce((sum, o) => sum + (o.amount || 0), 0);
  const successRate =
    orders.length > 0
      ? Math.round((successOrders.length / orders.length) * 100)
      : 0;
  const uniqueStudents = new Set(successOrders.map((o) => o.userId)).size;

  /* ── Table columns ── */
  const columns: Column<Order>[] = [
    {
      key: "user",
      header: "Student",
      render: (r) => (
        <div className="flex items-center gap-3 min-w-[180px]">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border bg-primary/10 text-sm font-bold uppercase text-primary">
            {r.user?.name?.charAt(0) || "U"}
          </div>
          <div>
            <span className="block font-semibold text-foreground leading-tight">
              {r.user?.name || "Unknown"}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {r.user?.mobile || r.user?.email || "—"}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "plan",
      header: "Plan Purchased",
      render: (r) => (
        <div className="min-w-[160px]">
          <span className="block font-semibold text-foreground leading-tight">
            {r.plan?.title || "Unknown Plan"}
          </span>
          <div className="mt-1 flex items-center gap-1.5">
            {r.plan?.type && (
              <span className="inline-block rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                {r.plan.type}
              </span>
            )}
            {r.plan?.durationDays && (
              <span className="text-[10px] text-muted-foreground">
                {r.plan.durationDays}d
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      render: (r) => (
        <span className="font-bold text-base text-foreground">₹{r.amount}</span>
      ),
    },
    {
      key: "paymentStatus",
      header: "Status",
      render: (r) => <StatusBadge status={r.paymentStatus} />,
    },
    {
      key: "isActive",
      header: "Access",
      render: (r) =>
        r.isActive ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">
            ✓ Active
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
            Inactive
          </span>
        ),
    },
    {
      key: "paymentMethod",
      header: "Method",
      render: (r) => (
        <span className="text-sm text-muted-foreground">
          {r.paymentMethod || "—"}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: "Order Date",
      render: (r) => (
        <div className="text-sm">
          <span className="block font-medium text-foreground">
            {format(new Date(r.createdAt), "dd MMM yyyy")}
          </span>
          <span className="text-xs text-muted-foreground">
            {format(new Date(r.createdAt), "hh:mm a")}
          </span>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-muted/20">
      <PageHeader
        title="Order History"
        description="Track student purchases and manage subscription assignments."
        action={
          <Button
            variant="outline"
            size="icon"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Refresh"
          >
            <RotateCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
          </Button>
        }
      />

      <div className="space-y-6 p-4 sm:p-6 lg:p-8">

        {/* ── Stats Row ── */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            label="Total Orders"
            value={totalOrders}
            icon={ShoppingBag}
            color="bg-blue-500/10 text-blue-600"
            sub="All time"
          />
          <StatCard
            label="Total Revenue"
            value={`₹${totalRevenue.toLocaleString("en-IN")}`}
            icon={IndianRupee}
            color="bg-emerald-500/10 text-emerald-600"
            sub="Successful payments"
          />
          <StatCard
            label="Success Rate"
            value={`${successRate}%`}
            icon={TrendingUp}
            color="bg-violet-500/10 text-violet-600"
            sub={`${successOrders.length} successful`}
          />
          <StatCard
            label="Subscribed Students"
            value={uniqueStudents}
            icon={Users}
            color="bg-amber-500/10 text-amber-600"
            sub="Unique buyers"
          />
        </div>

        {/* ── Filters ── */}
        <div className="flex flex-col gap-4 rounded-xl border bg-card p-4 shadow-sm lg:flex-row lg:items-center">
          <SearchBar
            value={list.search}
            onChange={list.setSearch}
            placeholder="Search by student name, plan, transaction ID..."
            className="flex-1"
          />
          <div className="flex flex-wrap items-center gap-3">
            {/* Payment status filter */}
            <Select
              value={list.filters.paymentStatus ?? "all"}
              onValueChange={(v) =>
                list.setFilter("paymentStatus", v === "all" ? undefined : v)
              }
            >
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="Payment Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Success">
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Success
                  </span>
                </SelectItem>
                <SelectItem value="Pending">
                  <span className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-amber-500" /> Pending
                  </span>
                </SelectItem>
                <SelectItem value="Failed">
                  <span className="flex items-center gap-2">
                    <XCircle className="h-3.5 w-3.5 text-red-500" /> Failed
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Plan filter */}
            <Select
              value={list.filters.planId ?? "all"}
              onValueChange={(v) =>
                list.setFilter("planId", v === "all" ? undefined : v)
              }
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Filter by Plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                {plansData?.data?.map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Active access filter */}
            <Select
              value={
                list.filters.isActive === undefined
                  ? "all"
                  : list.filters.isActive === true
                  ? "true"
                  : "false"
              }
              onValueChange={(v) =>
                list.setFilter(
                  "isActive",
                  v === "all" ? undefined : v === "true"
                )
              }
            >
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="Access Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Access</SelectItem>
                <SelectItem value="true">Active Access</SelectItem>
                <SelectItem value="false">Inactive / Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* ── Table ── */}
        <DataTable
          columns={columns}
          rows={orders}
          loading={isFetching}
          total={(data as any)?.total ?? orders.length}
          page={list.page}
          limit={list.limit}
          onPageChange={list.setPage}
          onLimitChange={list.setLimit}
          emptyText="No orders found. Purchases made by students will appear here."
          actions={(row) => (
            <div className="flex justify-end gap-1">
              <Link to={`/orders/${row.id}`}>
                <Button size="icon" variant="ghost" title="View Details">
                  <Eye className="h-4 w-4" />
                </Button>
              </Link>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => openEdit(row)}
                title="Edit Order"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setToDelete(row)}
                className="text-destructive hover:text-destructive"
                title="Delete Order"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        />
      </div>

      {/* ── Edit Dialog ── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl shadow-xl border-none p-0 overflow-hidden rounded-xl flex flex-col max-h-[85vh]">
          <DialogHeader className="p-8 pb-4 bg-primary/5 text-left shrink-0">
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <Receipt className="h-6 w-6 text-primary" />
              Edit Order
            </DialogTitle>
            <DialogDescription className="font-medium text-muted-foreground">
              Update payment or subscription status for this order.
            </DialogDescription>
          </DialogHeader>

          {/* Student & Plan summary */}
          {editing && (
            <div className="px-8 pt-4 shrink-0">
              <div className="flex items-center gap-3 rounded-xl border bg-muted/30 p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary font-bold uppercase text-sm">
                  {editing.user?.name?.charAt(0) || "U"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground leading-tight">
                    {editing.user?.name || "Unknown Student"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {editing.user?.email}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    Plan
                  </p>
                  <p className="text-sm font-semibold text-foreground">
                    {editing.plan?.title || "—"}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="px-8 py-4 flex-1 overflow-y-auto">
            <form id="order-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount" className="text-sm font-semibold text-foreground">
                    Amount (₹)
                  </Label>
                  <Input
                    id="amount"
                    type="number"
                    className="h-11 shadow-sm"
                    {...form.register("amount", { valueAsNumber: true })}
                  />
                  {form.formState.errors.amount && (
                    <p className="text-xs font-medium text-destructive">
                      {form.formState.errors.amount.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Payment Status
                  </Label>
                  <Select
                    value={form.watch("paymentStatus")}
                    onValueChange={(v: any) => form.setValue("paymentStatus", v)}
                  >
                    <SelectTrigger className="h-11 shadow-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Success">✓ Success</SelectItem>
                      <SelectItem value="Pending">⏳ Pending</SelectItem>
                      <SelectItem value="Failed">✗ Failed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="paymentMethod" className="text-sm font-semibold text-foreground">
                    Payment Method
                  </Label>
                  <Input
                    id="paymentMethod"
                    placeholder="e.g. Cash, UPI, Manual"
                    className="h-11 shadow-sm"
                    {...form.register("paymentMethod")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transactionId" className="text-sm font-semibold text-foreground">
                    Transaction ID
                  </Label>
                  <Input
                    id="transactionId"
                    placeholder="e.g. TXN12345"
                    className="h-11 shadow-sm"
                    {...form.register("transactionId")}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">
                  Subscription Access
                </Label>
                <Select
                  value={form.watch("isActive") ? "true" : "false"}
                  onValueChange={(v) => form.setValue("isActive", v === "true")}
                >
                  <SelectTrigger className="h-11 shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">✓ Active — Student can access plan</SelectItem>
                    <SelectItem value="false">✗ Inactive / Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </form>
          </div>

          <DialogFooter className="border-t p-6 bg-muted/10 shrink-0 gap-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              className="h-11 px-6 font-bold uppercase tracking-tight text-xs"
            >
              Cancel
            </Button>
            <Button
              form="order-form"
              type="submit"
              disabled={updateM.isLoading}
              className="h-11 px-8 rounded-xl font-bold uppercase tracking-wider text-xs shadow-xl shadow-primary/20"
            >
              {updateM.isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirm ── */}
      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          await deleteOrder(toDelete.id).unwrap();
          toast.success("Order permanently removed");
          setToDelete(null);
        }}
        loading={deleteM.isLoading}
        title="Delete Order?"
        description={`This will permanently remove the order for "${toDelete?.user?.name || "this student"}". The student may lose access to the "${toDelete?.plan?.title || "plan"}" if this was their only purchase.`}
      />
    </div>
  );
}
